import { getCurrencySymbol } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { AccountGroup } from '@galaxy/account-group';
import {
  ItemPricingTableConfig,
  ItemsChangedEvent,
  ItemSelectorFilterType,
  WholesaleSummary,
} from '@galaxy/inventory-ui';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { LineItem, Revenue, RevenueComponent, RevenuePeriod, SalesOrdersService } from '@vendasta/sales-orders';
import { OrderFormOptionsInterface } from '@vendasta/store';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { UILineItemWithEditionPricing } from '../../shared/interface';
import { PeriodOptions } from './line-items.interface';
import { LineItemsService } from './line-items.service';
import { inventoryItemsToLineItems } from './utils';
import { OrderPermissionsService } from '../../core/permissions/permissions.service';
import { OrderAction } from '../../core/permissions/permissions';

@Component({
  selector: 'orders-line-items',
  templateUrl: './line-items.component.html',
  styleUrls: ['./line-items.component.scss'],
  standalone: false,
})
export class LineItemsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() business: Pick<AccountGroup, 'externalIdentifiers' | 'napData' | 'accountGroupId'>;
  // marketId on the order could be different than the current marketId on the account group
  @Input() orderMarketId: string;

  // we specify line items to populate the component when an opportunity is
  // selected, or when we are editing an existing order
  @Input() specifiedLineItems: LineItem[];

  // configurations
  hideSellingStandaloneProducts = false;
  // allows multiple quantities to be set for products that are not multi-activatable
  @Input() bypassQuantityRestrictions = false;
  @Input() allowNegativeValues = false;
  @Input() editable = false;
  @Input() startEditing = false;
  @Input() currencyOverride: string = null;
  @Input() allowRemovingLastLineItem: boolean;
  @Input() viewOnly = false;
  @Input() canEditInvoices = false;
  @Input() canSeeWholesaleColumn = false;

  @Output() lineItemsUpdated: EventEmitter<UILineItemWithEditionPricing[]> = new EventEmitter<
    UILineItemWithEditionPricing[]
  >();
  @Output() lineItemsInitialized: EventEmitter<UILineItemWithEditionPricing[]> = new EventEmitter<
    UILineItemWithEditionPricing[]
  >();
  @Output() updateWholesaleSummary: EventEmitter<WholesaleSummary> = new EventEmitter<WholesaleSummary>();

  editing = false;
  loading$: Observable<boolean>;
  formArrayLineItems: UntypedFormArray;
  currentLineItems$$: BehaviorSubject<UILineItemWithEditionPricing[]> = new BehaviorSubject<
    UILineItemWithEditionPricing[]
  >([]);
  currentLineItems: UILineItemWithEditionPricing[] = [];
  uiLineItems$: Observable<UILineItemWithEditionPricing[]>;
  orderFormOptions$: Observable<OrderFormOptionsInterface>;

  itemPricingTableConfig$: Observable<ItemPricingTableConfig>;

  // pricing related
  periodOptions: PeriodOptions[] = [
    { label: 'FRONTEND.STORE.PERIODS.ONE_TIME', value: RevenuePeriod.ONETIME },
    { label: 'FRONTEND.STORE.PERIODS.MONTHLY', value: RevenuePeriod.MONTHLY },
    { label: 'FRONTEND.STORE.PERIODS.YEARLY', value: RevenuePeriod.YEARLY },
  ];

  oneTimeOnlyOptions: PeriodOptions[] = [{ label: 'FRONTEND.STORE.PERIODS.ONE_TIME', value: RevenuePeriod.ONETIME }];

  private subscriptions: Subscription[] = [];
  private readonly canSellStandaloneProducts$ = this.permissionsService.CanDoAction(
    OrderAction.CanSellStandaloneProducts,
  );

  constructor(
    private formbuilder: UntypedFormBuilder,
    private cdk: ChangeDetectorRef,
    private alertService: SnackbarService,
    private readonly salesOrdersService: SalesOrdersService,
    private readonly lineItemsService: LineItemsService,
    private readonly permissionsService: OrderPermissionsService,
  ) {}

  ngOnInit(): void {
    this.editing = !!this.editable && !!this.startEditing;
    this.lineItemsService.initialize(this.business, this.specifiedLineItems);
    this.uiLineItems$ = this.lineItemsService.uiLineItems$.pipe(shareReplay(1));
    this.loading$ = this.lineItemsService.loading$;
    // use the AccountGroup marketId for the current sales order configuration settings
    const orderConfig$ = this.salesOrdersService
      .getConfig(this.business.externalIdentifiers.partnerId, this.business.externalIdentifiers.marketId)
      .pipe(shareReplay(1));
    const hideSellingStandaloneProducts$ = this.canSellStandaloneProducts$.pipe(
      map((canSellStandaloneProducts) => !canSellStandaloneProducts),
    );
    this.subscriptions.push(
      hideSellingStandaloneProducts$.subscribe((hideSellingStandaloneProducts) => {
        this.hideSellingStandaloneProducts = hideSellingStandaloneProducts;
      }),
    );
    this.formArrayLineItems = this.formbuilder.array([]);

    this.subscriptions.push(
      this.uiLineItems$
        .pipe(
          map((uiLineItems) => {
            this.setCurrentLineItems(uiLineItems);
            this.lineItemsInitialized.emit(uiLineItems);
            this.formArrayLineItems = this.buildFormArrayFromLineItems(uiLineItems);
            this.cdk.detectChanges();
          }),
          tap(() => this.lineItemsService.loading$$.next(false)),
        )
        .subscribe(),
    );
    this.orderFormOptions$ = orderConfig$.pipe(
      map((config): OrderFormOptionsInterface => {
        return {
          readOnly: false,
          showOfficeUseQuestions: true,
          bypassRequiredQuestions: !config.salespersonOptions.validateRequiredFields,
        };
      }),
    );

    this.itemPricingTableConfig$ = combineLatest([
      hideSellingStandaloneProducts$,
      this.lineItemsService.contractStartDate$,
    ]).pipe(
      map(([hideSellingStandaloneProducts, startDate]) =>
        this.pricingTableConfig(hideSellingStandaloneProducts, startDate),
      ),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.specifiedLineItems?.currentValue?.length) {
      if (changes.specifiedLineItems?.currentValue?.length === changes.specifiedLineItems?.previousValue?.length) {
        if (
          changes.specifiedLineItems?.currentValue?.some(
            (elem, i) => elem?.cost?.customPrice !== changes.specifiedLineItems?.previousValue?.[i].cost?.customPrice,
          )
        ) {
          this.lineItemsService.initialize(this.business, changes.specifiedLineItems.currentValue);
        }
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  deleteLineItem(index: number): void {
    if (!this.allowRemovingLastLineItem && this.currentLineItems.length === 1) {
      this.alertService.openErrorSnack('LIB_ORDERS.COMMON.ORDERS.ERROR_AT_LEAST_ONE_LINE_ITEM');
      return;
    }
    this.currentLineItems.splice(index, 1);
    this.setCurrentLineItems(this.currentLineItems);
    this.formArrayLineItems.removeAt(index);
  }

  duplicateLineItem(index: number): void {
    const duplicateLineItem = structuredClone(this.currentLineItems[index]);
    this.currentLineItems.splice(index + 1, 0, duplicateLineItem);
    this.setCurrentLineItems(this.currentLineItems);
    // update line items in the UI and form
    this.formArrayLineItems = this.buildFormArrayFromLineItems(this.currentLineItems);
    this.cdk.detectChanges();
    this.lineItemsUpdated.emit(this.currentLineItems);
  }

  // builds the form group out of the selected items
  buildFormArrayFromLineItems(lineItems: UILineItemWithEditionPricing[]): UntypedFormArray {
    const newForm = this.formbuilder.array(
      lineItems.map((li) => {
        const revenueControls = this.revenueControlFromLineItemRevenue(li.lineItem?.currentRevenue);
        const quantityEnabled = this.bypassQuantityRestrictions || (li.isMultiActivatable && !li.usesCustomPricing);
        return this.formbuilder.group({
          quantity: [
            {
              value: quantityEnabled ? li.lineItem.quantity : 1,
              disabled: !quantityEnabled,
            },
            [Validators.min(1)],
          ],
          revenueComponents: this.formbuilder.array(revenueControls),
        });
      }),
    );
    return newForm;
  }

  revenueControlFromLineItemRevenue(revenue: Revenue): UntypedFormGroup[] {
    return (revenue?.revenueComponents || []).map((rc) =>
      this.formbuilder.group({
        revenue: [(rc.value || 0) / 100, [Validators.required, this.validateRevenue.bind(this)]],
        period: [rc.period === undefined ? RevenuePeriod.ONETIME : rc.period, [Validators.required]],
        startingAt: [rc.isStartingRevenue],
      }),
    );
  }

  validateRevenue(c: UntypedFormControl): ValidationErrors | null {
    const revenueValue = c.value;
    if (isNaN(revenueValue)) {
      return { pattern: true };
    }
    if (Number(revenueValue) < 0 && this.allowNegativeValues === false) {
      return { min: true };
    }
    return null;
  }

  //
  // NOTE:
  // The following methods are used from page components with a ViewChild reference to this component
  //

  // check that the form of the component is in a valid state
  public isFormValid(): boolean {
    const valid = this.formArrayLineItems.valid;
    if (!valid) {
      this.formArrayLineItems.markAsTouched();
      this.formArrayLineItems.updateValueAndValidity();
    }
    if (this.currentLineItems.length <= 0) {
      this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_MUST_HAVE_ITEMS');
      return false;
    }
    if (this.currentLineItems.find((item) => item.lineItem?.currentRevenue?.revenueComponents.length === 0)) {
      this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERRORS.ERROR_MUST_HAVE_PRICING');
      return false;
    }

    return valid;
  }

  // retrieve the line items currently stored in the component
  public getCurrentLineItems(): LineItem[] {
    return this.currentLineItems.map((li) => {
      const item = new LineItem(li.lineItem);
      if (this.currencyOverride) {
        item.currencyCode = this.currencyOverride;
      }
      return item;
    });
  }

  public currencySymbol(currencyCode: string): string {
    return getCurrencySymbol(currencyCode, 'wide');
  }

  private setCurrentLineItems(uiLineItems: UILineItemWithEditionPricing[]): void {
    // Sort the revenue components to ensure one time always appears last, initially
    uiLineItems?.forEach?.((uilineItem) => {
      uilineItem.lineItem?.currentRevenue?.revenueComponents?.sort((a: RevenueComponent, b: RevenueComponent) => {
        if (a.period === RevenuePeriod.ONETIME || !a.period) {
          return 1;
        }
        if (b.period === RevenuePeriod.ONETIME || !b.period) {
          return -1;
        }
        return 0;
      });
    });
    this.currentLineItems = uiLineItems;
    this.currentLineItems$$.next(uiLineItems);
  }

  updateLineItemsFromInventoryLineItems(changedEvent: ItemsChangedEvent): void {
    let updateFn: (currentLineItems: UILineItemWithEditionPricing[]) => UILineItemWithEditionPricing[];
    switch (changedEvent.type) {
      case 'add':
        updateFn = (currentLineItems) => {
          const newItems = changedEvent.items;
          const newLineItems = inventoryItemsToLineItems(newItems);
          newItems.forEach((newItem, i) => {
            currentLineItems.splice(newItem.index, 0, newLineItems[i]);
          });
          return currentLineItems;
        };
        break;
      case 'update':
        updateFn = (currentLineItems) => {
          const changedItems = changedEvent.items;
          const changedLineItems = inventoryItemsToLineItems(changedItems);
          changedItems.forEach((changedItem, i) => {
            const lineItem = changedLineItems[i];
            currentLineItems[changedItem.index] = lineItem;
          });
          return currentLineItems;
        };
        break;
      case 'overwrite':
        updateFn = () => {
          const newItems = changedEvent.items;
          const newLineItems = inventoryItemsToLineItems(newItems);
          return newLineItems;
        };
        break;
      case 'delete':
        changedEvent.items.forEach((item) => this.deleteLineItem(item.index));
        break;
    }
    if (updateFn) {
      this.setCurrentLineItems(updateFn(this.currentLineItems));
    }
    this.lineItemsUpdated.emit(this.currentLineItems);
  }

  private pricingTableConfig(
    hideSellingStandaloneProducts: boolean,
    billingTermsDefaultStartDate: Date,
  ): ItemPricingTableConfig {
    let itemTypesAvailable: ItemSelectorFilterType[] = ['products', 'packages'];
    if (hideSellingStandaloneProducts) {
      itemTypesAvailable = itemTypesAvailable.filter((itemType) => itemType !== 'products');
    }

    let hideColumns = [];
    let hideWholesale = false;
    if (!this.canSeeWholesaleColumn) {
      hideColumns = ['wholesale_price'];
      hideWholesale = true;
    }

    return {
      partnerId: this.business.externalIdentifiers.partnerId,
      marketId: this.orderMarketId,
      businessId: this.business.accountGroupId,
      pageSize: 15,
      hideOwnerName: true,
      hideWholesale: hideWholesale,
      hideRetail: false,
      hideQuantity: false,
      hideItemsWithOrderForms: false,
      itemTypesAvailable: itemTypesAvailable,
      enforceDependencies: true,
      quantityEditable: true,
      billingTermsConfig: {
        enabled: true,
        defaultStartDate: billingTermsDefaultStartDate,
      },
      hideColumns: hideColumns,
      hidePriceSummary: true,
      hideBorder: true,
      canAddItems: true,
      canEditInvoices: this.canEditInvoices,
      canScheduleActivations: true,
    };
  }

  public onUpdateWholesaleSummary(wholesaleSummary: WholesaleSummary): void {
    this.updateWholesaleSummary.emit(wholesaleSummary);
  }
}
